import React from 'react';
import { intersectLiteral } from '@/hooks/useServiceMasters';

/**
 * Default Status pre-selection for Vehicle Outwards.
 * Vehicle Inwards uses ALL statuses from the master.
 * Every other report tab (the 5 "operational" reports) uses ALL visible
 * statuses MINUS the excluded list below — computed at runtime from the
 * master so new statuses automatically flow into the default selection.
 *
 * Exported as a constant so callers can reference it elsewhere (e.g. tests),
 * but this list intentionally lives next to the hook that uses it.
 */
export const REPORTS_STATUS_DEFAULTS = [
  'Not Started', 'In Queue', 'WIP', 'Test Drive', 'Process Invoice',
  'Completed', 'Invoiced', 'Warranty',
];

/**
 * Status names excluded from the default pre-selection on the 5 operational
 * report tabs (Technician Wise, Job Status, Service Head, Job Type,
 * Supervisor Wise). Matched case-insensitively and trimmed.
 */
const OPERATIONAL_STATUS_EXCLUDE = new Set(['advance', 'invoice', 'invoiced', 'delivered']);

/** Tabs that land on the "all except excluded" status baseline by default. */
const OPERATIONAL_TABS = new Set(['technician', 'job-status', 'service-head', 'job-type', 'supervisor']);

const EMPTY_MULTI_REST = {
  service_head: [], job_type: [], supervisor: [], technician: [],
};

/**
 * Returns the default doc_type selection for the 5 operational tabs:
 *   all visible statuses MINUS OPERATIONAL_STATUS_EXCLUDE.
 * Kept pure (no hooks, no effects) so it can be used both inside the mount
 * effect and by applyStatusDefault.
 */
function operationalStatusDefault(statusMaster) {
  return (statusMaster || []).filter(
    (name) => !OPERATIONAL_STATUS_EXCLUDE.has(String(name || '').trim().toLowerCase())
  );
}

/**
 * useServiceReportsFilters
 *
 * Owns the per-tab filter state for `/service/reports`:
 *   • `multiFilters` (Status / Service Head / Job Type / Supervisor / Technician)
 *   • `showPdi` toggle
 *   • `showDelivered` toggle
 *
 * Defaults per tab (applied on first visit; snapshot restored on revisit):
 *   • vehicle-inwards          → ALL statuses, PDI = ON, Delivered = ON
 *   • vehicle-outwards         → REPORTS_STATUS_DEFAULTS, PDI = ON, Delivered = ON
 *   • technician / job-status / service-head / job-type / supervisor
 *                              → all visible statuses EXCEPT {Advance, Invoice,
 *                                Invoiced, Delivered}, PDI = OFF, Delivered = OFF
 *
 * Per-tab session memory: user's filter selections are snapshotted on tab
 * change and restored when the user navigates back.
 */
export function useServiceReportsFilters({ activeTab, masters }) {
  const [multiFilters, setMultiFilters] = React.useState({
    doc_type: [], ...EMPTY_MULTI_REST,
  });
  const [showPdi, setShowPdi] = React.useState(false);
  const [showDelivered, setShowDelivered] = React.useState(false);

  // iter-120: Per-tab session memory now backed by sessionStorage so filters
  // survive component unmounts (edit/view round-trips, router navigation).
  // Previously lived in a `useRef` which reset on each mount.
  const STORAGE_KEY = 'service:reports:tab-snapshots';
  const SCHEMA_VERSION = 1;
  const tabSnapshotsRef = React.useRef(null);
  if (tabSnapshotsRef.current === null) {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      tabSnapshotsRef.current = (parsed && parsed.__v === SCHEMA_VERSION && parsed.snapshots) || {};
    } catch {
      tabSnapshotsRef.current = {};
    }
  }
  const persistSnapshots = React.useCallback(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ __v: SCHEMA_VERSION, snapshots: tabSnapshotsRef.current })
      );
    } catch { /* noop */ }
  }, []);

  const lastActiveTabRef = React.useRef('');
  // Mirrors the latest filter values into a ref so the tab-change effect can
  // read them without listing them as dependencies (avoids feedback loops).
  const filtersSnapshotRef = React.useRef(null);
  filtersSnapshotRef.current = { multiFilters, showPdi, showDelivered };

  // iter-120: Continuously persist the current tab's snapshot on every filter
  // change so a mid-work unmount (user clicks Edit on a row) still saves state.
  React.useEffect(() => {
    if (!activeTab || masters.loading) return;
    tabSnapshotsRef.current[activeTab] = { multiFilters, showPdi, showDelivered };
    persistSnapshots();
  }, [activeTab, multiFilters, showPdi, showDelivered, masters.loading, persistSnapshots]);

  React.useEffect(() => {
    if (masters.loading) return;
    const prevTab = lastActiveTabRef.current;

    // Save outgoing tab's filters
    if (prevTab && prevTab !== activeTab && filtersSnapshotRef.current) {
      tabSnapshotsRef.current[prevTab] = filtersSnapshotRef.current;
      persistSnapshots();
    }

    // Restore incoming tab if seen before
    const saved = tabSnapshotsRef.current[activeTab];
    if (saved) {
      setMultiFilters(saved.multiFilters);
      setShowPdi(saved.showPdi);
      setShowDelivered(saved.showDelivered);
    } else if (activeTab === 'vehicle-inwards') {
      setMultiFilters({ doc_type: [...(masters.status || [])], ...EMPTY_MULTI_REST });
      setShowPdi(true);
      setShowDelivered(true);
    } else if (activeTab === 'vehicle-outwards') {
      const matched = intersectLiteral(masters.status, REPORTS_STATUS_DEFAULTS);
      setMultiFilters({ doc_type: matched, ...EMPTY_MULTI_REST });
      setShowPdi(true);
      setShowDelivered(true);
    } else if (OPERATIONAL_TABS.has(activeTab)) {
      setMultiFilters({ doc_type: operationalStatusDefault(masters.status), ...EMPTY_MULTI_REST });
      setShowPdi(false);
      setShowDelivered(false);
    } else {
      // Future tabs — fall back to the legacy REPORTS_STATUS_DEFAULTS behaviour
      const matched = intersectLiteral(masters.status, REPORTS_STATUS_DEFAULTS);
      setMultiFilters({ doc_type: matched, ...EMPTY_MULTI_REST });
      setShowPdi(false);
      setShowDelivered(false);
    }

    lastActiveTabRef.current = activeTab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, masters.loading]);

  // Wrappers used by the Default button on the Status multi-filter.
  let statusDefaultButtonValues;
  if (activeTab === 'vehicle-inwards') {
    statusDefaultButtonValues = masters.status || [];
  } else if (OPERATIONAL_TABS.has(activeTab)) {
    statusDefaultButtonValues = operationalStatusDefault(masters.status);
  } else {
    statusDefaultButtonValues = REPORTS_STATUS_DEFAULTS;
  }

  const applyStatusDefault = React.useCallback(() => {
    if (activeTab === 'vehicle-inwards') {
      setMultiFilters((f) => ({ ...f, doc_type: [...(masters.status || [])] }));
    } else if (OPERATIONAL_TABS.has(activeTab)) {
      setMultiFilters((f) => ({ ...f, doc_type: operationalStatusDefault(masters.status) }));
    } else {
      const matched = intersectLiteral(masters.status, REPORTS_STATUS_DEFAULTS);
      setMultiFilters((f) => ({ ...f, doc_type: matched }));
    }
  }, [activeTab, masters.status]);

  return {
    multiFilters, setMultiFilters,
    showPdi, setShowPdi,
    showDelivered, setShowDelivered,
    statusDefaultButtonValues,
    applyStatusDefault,
  };
}
